import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLoadingMessage } from "src/constants/loadingMessages";
import useGlobalLoading from "src/hooks/useGlobalLoading";
import { useSelector } from "src/store";
import axiosInstance from "src/utils/axios";

const useAsyncRequest = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { adminMode } = useSelector((state) => state.settings);
  const { identitySelected } = useSelector((state) => state.users);
  const [isAsyncLoading, setAsyncIsLoading] = useState(false);
  const [isDataError, setIsDataError] = useState(false);
  const [res, setRes] = useState(null);
  const { t } = useTranslation();
  const { begin, end } = useGlobalLoading("useAsyncRequest");

  const handleLoadingState = useCallback(
    (isLoading, label, source) => {
      setAsyncIsLoading(isLoading);

      if (isLoading) {
        return begin({
          source: source || `useAsyncRequest:${label || "request"}`,
          message:
            label ||
            getLoadingMessage("loadingData", `${t("Obteniendo datos")}...`),
          metadata: { hook: "useAsyncRequest" },
        });
      }

      if (source) {
        end({ source, metadata: { hook: "useAsyncRequest" } });
      }
    },
    [begin, end, t],
  );

  const handleSnackbar = useCallback(
    (message, variant = "info", sb) => {
      if (sb) closeSnackbar(sb);
      if (message) enqueueSnackbar(message, { variant });
    },
    [closeSnackbar, enqueueSnackbar],
  );

  const handleError = useCallback(
    async (error, label, onBackground, sb) => {
      setIsDataError(true);
      console.error(error);
      if (!onBackground) handleLoadingState(false);
      if (sb) closeSnackbar(sb);

      if (onBackground) {
        return error;
      }

      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const json = JSON.parse(reader.result);
            handleSnackbar(json.message || `${label} : error`, "error");
          } catch (e) {
            handleSnackbar(`${label} : error`, "error");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        handleSnackbar(
          error.data?.message || error.message || `${label} : error`,
          "error",
        );
      }
      return error;
    },
    [closeSnackbar, handleLoadingState, handleSnackbar],
  );

  const loadData = useCallback(
    async (url, data, options, label, customMessage, onBackground) => {
      if (!onBackground && isAsyncLoading) return;

      let payload = data;
      const resolvedMode = adminMode ? "admin" : "user";

      if (payload instanceof FormData) {
        const hasCustomer = payload.has("customer_id");
        const hasMode = payload.has("mode");
        if (hasCustomer && !hasMode) {
          payload.append("mode", resolvedMode);
        }
      } else if (
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload)
      ) {
        const hasCustomer = payload?.customer_id != null;
        if (hasCustomer && payload.mode == null) {
          payload = {
            ...payload,
            mode: resolvedMode,
          };
        }
      }

      const shouldAttachIdentity =
        !adminMode &&
        identitySelected?.id &&
        payload &&
        url &&
        !url.includes("/identity/available/") &&
        !url.includes("/identity/session/") &&
        !url.includes("/identity/setSessionIdentity/") &&
        !url.includes("/dinvoice/invoice/generation/");

      if (shouldAttachIdentity && payload instanceof FormData) {
        const mode = payload.get("mode");
        const hasIdentity = payload.has("identity_id");
        const hasCustomer = payload.has("customer_id");
        if (mode !== "admin" && hasCustomer && !hasIdentity) {
          payload.append("identity_id", identitySelected.id);
        }
      } else if (
        shouldAttachIdentity &&
        typeof payload === "object" &&
        !Array.isArray(payload)
      ) {
        const mode = payload?.mode;
        const hasCustomer = payload?.customer_id != null;
        if (mode !== "admin" && hasCustomer && payload.identity_id == null) {
          payload = {
            ...payload,
            identity_id: identitySelected.id,
          };
        }
      }

      const loadingSource = `useAsyncRequest:${label || url || "unknown"}`;
      if (!onBackground) handleLoadingState(true, label, loadingSource);
      const sb =
        label && !onBackground
          ? enqueueSnackbar(customMessage || `${t("Obteniendo")} ${label}`, {
              variant: "info",
            })
          : null;

      try {
        const response = await axiosInstance.post(url, payload, options);
        const isDvaPending = response?.data?.code === "approval_pending";
        if (!onBackground) {
          handleLoadingState(false, null, loadingSource);
          // El interceptor global de DVA ya notifica cuando queda pendiente,
          // evitar mostrar aqu� un mensaje de �xito contradictorio.
          if (!isDvaPending) {
            handleSnackbar(
              label ? `${label} ${t("cargado exitosamente.")}` : null,
              "success",
              sb,
            );
          } else if (sb) {
            closeSnackbar(sb);
          }
        }
        return response;
      } catch (error) {
        if (!onBackground) {
          handleLoadingState(false, null, loadingSource);
        }
        return handleError(error, label, onBackground, sb);
      }
    },
    [
      adminMode,
      enqueueSnackbar,
      handleError,
      handleLoadingState,
      identitySelected,
      isAsyncLoading,
      t,
    ],
  );

  const requestData = useCallback(
    async (url, data, options, label, customMessage, onBackground) => {
      const response = await loadData(
        url,
        data,
        options,
        label,
        customMessage,
        onBackground,
      );
      setRes(
        response.data?.res === 1
          ? { ...response.data, status: response.status }
          : response,
      );
    },
    [loadData],
  );

  const api = useCallback(
    async (url, data, options, label, onBackground) => {
      return loadData(url, data, options, label, null, onBackground);
    },
    [loadData],
  );

  return {
    res,
    requestData,
    isLoading: isAsyncLoading,
    api,
  };
};

export default useAsyncRequest;
